import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, Settings, Maximize, AlertCircle } from 'lucide-react';

const VideoPlayer = ({ videoUrl, title, autoPlay = true }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showSettings, setShowSettings] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const controlsTimeoutRef = useRef(null);
  const saveProgressIntervalRef = useRef(null);

  // ✅ Use refs for values used inside keydown — avoids stale closure
  const isPlayingRef = useRef(false);
  const volumeRef = useRef(1);
  const currentTimeRef = useRef(0);
  const durationRef = useRef(0);
  const videoErrorRef = useRef(false);

  // Keep refs in sync
  useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);
  useEffect(() => { volumeRef.current = volume; }, [volume]);
  useEffect(() => { currentTimeRef.current = currentTime; }, [currentTime]);
  useEffect(() => { durationRef.current = duration; }, [duration]);
  useEffect(() => { videoErrorRef.current = videoError; }, [videoError]);

  const getVideoIdFromUrl = () => {
    const pathParts = window.location.pathname.split('/');
    return pathParts[pathParts.length - 1];
  };

  const getCurrentVideoImage = () => {
    const storedImage = sessionStorage.getItem(`video_${getVideoIdFromUrl()}_image`);
    return storedImage || 'https://images.unsplash.com/photo-1574267432644-f610f5c2f56d?auto=format&fit=crop&w=800';
  };

  const loadSavedProgress = () => {
    const videoId = getVideoIdFromUrl();
    const watchHistory = JSON.parse(localStorage.getItem('continueWatching') || '[]');
    const savedVideo = watchHistory.find(item => item.id === videoId);
    return savedVideo?.currentTime || 0;
  };

  // ✅ useCallback so saveWatchProgress is stable
  const saveWatchProgress = useCallback(() => {
    if (videoRef.current && durationRef.current > 0) {
      const progress = (currentTimeRef.current / durationRef.current) * 100;
      const videoId = getVideoIdFromUrl();
      const watchData = {
        id: videoId, title,
        currentTime: currentTimeRef.current,
        duration: durationRef.current,
        progress: Math.round(progress),
        videoUrl,
        lastWatched: new Date().toISOString(),
        image: getCurrentVideoImage()
      };
      const existing = JSON.parse(localStorage.getItem('continueWatching') || '[]');
      const filtered = existing.filter(item => item.id !== videoId);
      localStorage.setItem('continueWatching', JSON.stringify([watchData, ...filtered].slice(0, 10)));
    }
  }, [title, videoUrl]);

  // ✅ togglePlay using ref — no stale closure
  const togglePlay = useCallback(() => {
    if (videoRef.current && !videoErrorRef.current) {
      if (isPlayingRef.current) {
        videoRef.current.pause();
      } else {
        videoRef.current.play().catch(() => {});
      }
    }
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      const el = videoRef.current?.parentElement;
      if (el?.requestFullscreen) el.requestFullscreen();
      else if (el?.webkitRequestFullscreen) el.webkitRequestFullscreen(); // Safari/TV
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) document.exitFullscreen();
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  const showControlsTemporarily = useCallback(() => {
    setShowControls(true);
    if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    controlsTimeoutRef.current = setTimeout(() => {
      if (isPlayingRef.current) setShowControls(false);
    }, 4000);
  }, []);

  // ✅ TV Remote D-pad — fully stable, no stale closures
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!videoRef.current) return;

      showControlsTemporarily();

      switch (e.key) {
        case 'Enter':
        case ' ':
        case 'MediaPlayPause':
          e.preventDefault();
          togglePlay();
          break;

        case 'ArrowRight':
        case 'MediaFastForward':
          e.preventDefault();
          if (!videoErrorRef.current) {
            videoRef.current.currentTime = Math.min(
              videoRef.current.currentTime + 10,
              durationRef.current
            );
          }
          break;

        case 'ArrowLeft':
        case 'MediaRewind':
          e.preventDefault();
          if (!videoErrorRef.current) {
            videoRef.current.currentTime = Math.max(
              videoRef.current.currentTime - 10,
              0
            );
          }
          break;

        case 'ArrowUp':
          e.preventDefault();
          if (!videoErrorRef.current) {
            const newVolUp = Math.min(1, parseFloat((volumeRef.current + 0.1).toFixed(1)));
            setVolume(newVolUp);
            videoRef.current.volume = newVolUp;
            setIsMuted(false);
            videoRef.current.muted = false;
          }
          break;

        case 'ArrowDown':
          e.preventDefault();
          if (!videoErrorRef.current) {
            const newVolDown = Math.max(0, parseFloat((volumeRef.current - 0.1).toFixed(1)));
            setVolume(newVolDown);
            videoRef.current.volume = newVolDown;
          }
          break;

        case 'Escape':
        case 'GoBack':
        case 'XF86Back':
          e.preventDefault();
          saveWatchProgress();
          window.history.back();
          break;

        case 'f':
        case 'F':
        case 'XF86ZoomIn':
          toggleFullscreen();
          break;

        case 'm':
        case 'M':
        case 'VolumeMute':
          e.preventDefault();
          if (videoRef.current) {
            const newMuted = !videoRef.current.muted;
            videoRef.current.muted = newMuted;
            setIsMuted(newMuted);
          }
          break;

        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [togglePlay, toggleFullscreen, saveWatchProgress, showControlsTemporarily]);

  // ✅ Video event listeners
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const updateTime = () => setCurrentTime(video.currentTime);

    const updateDuration = () => {
      setDuration(video.duration);
      setIsLoading(false);
      const savedPosition = loadSavedProgress();
      if (savedPosition > 0 && savedPosition < video.duration) {
        video.currentTime = savedPosition;
      }
      if (autoPlay) {
        video.play().then(() => setIsPlaying(true)).catch(() => {});
      }
    };

    const handleError = () => { setVideoError(true); setIsLoading(false); };
    const handleCanPlay = () => { setVideoError(false); setIsLoading(false); };
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => { setIsPlaying(false); saveWatchProgress(); };
    const handleWaiting = () => setIsLoading(true);
    const handlePlaying = () => setIsLoading(false);

    video.addEventListener('timeupdate', updateTime);
    video.addEventListener('loadedmetadata', updateDuration);
    video.addEventListener('error', handleError);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('waiting', handleWaiting);
    video.addEventListener('playing', handlePlaying);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);

    saveProgressIntervalRef.current = setInterval(() => {
      if (video && !video.paused) saveWatchProgress();
    }, 5000);

    return () => {
      video.removeEventListener('timeupdate', updateTime);
      video.removeEventListener('loadedmetadata', updateDuration);
      video.removeEventListener('error', handleError);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('waiting', handleWaiting);
      video.removeEventListener('playing', handlePlaying);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      if (saveProgressIntervalRef.current) clearInterval(saveProgressIntervalRef.current);
      saveWatchProgress();
    };
  }, [autoPlay, saveWatchProgress]);

  const handleProgressChange = (e) => {
    const newTime = parseFloat(e.target.value);
    setCurrentTime(newTime);
    if (videoRef.current) videoRef.current.currentTime = newTime;
  };

  const skipForward = () => { if (videoRef.current) videoRef.current.currentTime += 10; };
  const skipBackward = () => { if (videoRef.current) videoRef.current.currentTime -= 10; };

  const toggleMute = () => {
    if (videoRef.current) {
      const newMuted = !isMuted;
      videoRef.current.muted = newMuted;
      setIsMuted(newMuted);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
      setIsMuted(newVolume === 0);
    }
  };

  const changePlaybackRate = (rate) => {
    if (videoRef.current) {
      videoRef.current.playbackRate = rate;
      setPlaybackRate(rate);
      setShowSettings(false);
    }
  };

  const formatTime = (time) => {
    if (!time || isNaN(time)) return '0:00';
    const h = Math.floor(time / 3600);
    const m = Math.floor((time % 3600) / 60);
    const s = Math.floor(time % 60);
    if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const handleMouseMove = () => {
    showControlsTemporarily();
  };

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div
      className="relative w-full h-screen bg-black"
      onMouseMove={handleMouseMove}
      onMouseLeave={() => isPlaying && setShowControls(false)}
    >
      {/* ✅ TV Remote Hint — top right */}
      {showControls && !videoError && (
        <div className="absolute top-20 right-6 z-50 bg-black/70 text-white text-sm px-4 py-2 rounded-xl border border-white/10">
          ⬅ ➡ &nbsp;Skip 10s &nbsp;|&nbsp; ⬆ ⬇ &nbsp;Volume &nbsp;|&nbsp; OK &nbsp;Play/Pause &nbsp;|&nbsp; ⬅Back &nbsp;Exit
        </div>
      )}

      {/* Video Error Screen */}
      {videoError && (
        <div className="absolute inset-0 flex items-center justify-center bg-black z-50">
          <div className="text-center text-white">
            <AlertCircle className="w-20 h-20 mx-auto mb-4 text-red-500" />
            <h2 className="text-3xl font-bold mb-2">Video Not Available</h2>
            <p className="text-gray-400 mb-4 text-lg">The video source could not be loaded</p>
            <button
              onClick={() => window.history.back()}
              className="mt-6 bg-red-600 px-8 py-3 rounded-lg text-lg hover:bg-red-700 focus:ring-4 focus:ring-red-400 outline-none transition"
              autoFocus
            >
              Go Back
            </button>
          </div>
        </div>
      )}

      {/* Loading Spinner */}
      {isLoading && !videoError && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-40">
          <div className="animate-spin rounded-full h-20 w-20 border-t-4 border-b-4 border-red-600"></div>
        </div>
      )}

      {/* ✅ Video Element */}
      <video
        ref={videoRef}
        className="w-full h-full object-contain"
        src={videoUrl}
        onClick={togglePlay}
        playsInline
        preload="auto"
      />

      {/* ✅ Controls Overlay */}
      <div
        className={`absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent transition-opacity duration-300 pointer-events-none ${showControls ? 'opacity-100' : 'opacity-0'}`}
      >
        <div className="absolute bottom-0 left-0 right-0 px-8 pb-8 pointer-events-auto">

          {/* Progress Bar */}
          <div className="mb-4 relative group/bar">
            <input
              type="range"
              min="0"
              max={duration || 100}
              value={currentTime}
              onChange={handleProgressChange}
              disabled={videoError}
              className="w-full h-2 rounded-lg appearance-none cursor-pointer slider"
              style={{
                background: `linear-gradient(to right, #ef4444 0%, #ef4444 ${progressPercent}%, #4b5563 ${progressPercent}%, #4b5563 100%)`
              }}
            />
            {/* Time tooltip */}
            <div className="flex justify-between text-gray-400 text-sm mt-1">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>

          <div className="flex items-center justify-between">

            {/* Left Controls */}
            <div className="flex items-center space-x-6">

              {/* Play/Pause */}
              <button
                onClick={togglePlay}
                disabled={videoError}
                className="text-white hover:text-red-500 focus:text-red-500 focus:scale-125 outline-none transition-transform disabled:opacity-40"
                tabIndex={0}
              >
                {isPlaying
                  ? <Pause className="w-12 h-12" fill="white" />
                  : <Play className="w-12 h-12" fill="white" />
                }
              </button>

              {/* Skip Back 10s */}
              <button
                onClick={skipBackward}
                disabled={videoError}
                className="text-white hover:text-red-500 focus:scale-125 outline-none transition-transform disabled:opacity-40"
                tabIndex={0}
              >
                <SkipBack className="w-9 h-9" />
              </button>

              {/* Skip Forward 10s */}
              <button
                onClick={skipForward}
                disabled={videoError}
                className="text-white hover:text-red-500 focus:scale-125 outline-none transition-transform disabled:opacity-40"
                tabIndex={0}
              >
                <SkipForward className="w-9 h-9" />
              </button>

              {/* Volume */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={toggleMute}
                  disabled={videoError}
                  className="text-white hover:text-red-500 focus:scale-125 outline-none transition-transform disabled:opacity-40"
                  tabIndex={0}
                >
                  {isMuted || volume === 0
                    ? <VolumeX className="w-8 h-8" />
                    : <Volume2 className="w-8 h-8" />
                  }
                </button>
                <input
                  type="range" min="0" max="1" step="0.05"
                  value={isMuted ? 0 : volume}
                  onChange={handleVolumeChange}
                  disabled={videoError}
                  className="w-28 h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer"
                />
                <span className="text-white text-sm w-8">{Math.round((isMuted ? 0 : volume) * 100)}%</span>
              </div>

            </div>

            {/* Right Controls */}
            <div className="flex items-center space-x-5">

              {/* Playback Speed */}
              <div className="relative">
                <button
                  onClick={() => setShowSettings(s => !s)}
                  disabled={videoError}
                  className="text-white hover:text-red-500 focus:scale-125 outline-none transition-transform disabled:opacity-40"
                  tabIndex={0}
                >
                  <Settings className="w-7 h-7" />
                </button>
                {showSettings && (
                  <div className="absolute bottom-14 right-0 bg-black/95 rounded-xl p-3 min-w-[180px] border border-white/10 z-50">
                    <div className="text-white text-sm font-semibold mb-2 px-2">Playback Speed</div>
                    {[0.5, 0.75, 1, 1.25, 1.5, 2].map(rate => (
                      <button
                        key={rate}
                        onClick={() => changePlaybackRate(rate)}
                        className={`w-full text-left px-3 py-2 text-sm rounded-lg mb-1 transition ${
                          playbackRate === rate
                            ? 'bg-red-600 text-white'
                            : 'text-gray-300 hover:bg-white/10'
                        }`}
                      >
                        {rate === 1 ? '1x (Normal)' : `${rate}x`}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Fullscreen */}
              <button
                onClick={toggleFullscreen}
                disabled={videoError}
                className="text-white hover:text-red-500 focus:scale-125 outline-none transition-transform disabled:opacity-40"
                tabIndex={0}
              >
                <Maximize className="w-7 h-7" />
              </button>

            </div>
          </div>
        </div>
      </div>

      <style>{`
        .slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: #ef4444;
          cursor: pointer;
          border: 3px solid white;
          box-shadow: 0 0 6px rgba(0,0,0,0.6);
        }
        .slider::-moz-range-thumb {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: #ef4444;
          cursor: pointer;
          border: 3px solid white;
        }
        video:focus { outline: none; }
      `}</style>
    </div>
  );
};

export default VideoPlayer;